/* eslint-disable sonarjs/cognitive-complexity */
import { useMemo } from 'react'
import { Calendar, DateRangePicker, CalendarProps, DateRangeProps } from 'react-date-range'
import { Box, Button, Typography } from '@astro-ui/components'

import theme from '@astro-ui/theme'
import { DateFormatType, dateFormat, stringToDateObject } from '@astronautsid/wpe-utils'
import * as S from './DatePickerStyled'
import Fieldset from './components/Fieldset'
import MonthPicker from './components/MonthPicker'
import LeftMenu from './components/LeftMenu'
import useDatePicker, { DateRangeType, DateSingleType } from './useDatePicker'

export type DatePickerPropsType = (
  | (DateRangeType & Omit<DateRangeProps, 'onChange'>)
  | (DateSingleType & Omit<CalendarProps, 'onChange'>)
) & {
  title?: string
  monthPickerOnly?: boolean
  placeholder?: string
  hideLeftMenu?: boolean
  customIcon?: React.ReactNode
  errorMessage?: string
  months?: number
  maxRangeDays?: number
}

function DatePicker({
  title = 'Rentang Waktu',
  dateType,
  value,
  disabled,
  minDate,
  maxDate,
  leftMenuProp,
  defaultSelectedMenu,
  onChange,
  customSelectWeeklyRangeOnChange,
  monthPickerOnly,
  hideLeftMenu,
  placeholder = 'Pilih Tanggal',
  errorMessage,
  months = 2,
  maxRangeDays,
  ...rest
}: DatePickerPropsType & {
  disabled?: boolean
}) {
  const {
    isOpen,
    showDate,
    date,
    dateDefaultValue,
    anchorEl,
    menuSelected,
    dateSelected,
    dateRangeSelected,
    monthSelected,
    yearSelected,
    setMonthSelected,
    setYearSelected,
    handleOpen,
    handleClose,
    handleSelectMenu,
    handleSelectDate,
    handleSelectDateRange,
    handleReset,
    handleSubmit,
  } = useDatePicker({
    dateType,
    onChange,
    value,
    disabled,
    monthPickerOnly,
    minDate,
    maxDate,
    defaultSelectedMenu,
    leftMenuProp,
    dateDisplayFormat: monthPickerOnly ? `MMMM YYYY` : rest.dateDisplayFormat,
    dateDisplayFormatFrom: rest.dateDisplayFormatFrom,
    customSelectWeeklyRangeOnChange,
  } as DateSingleType | DateRangeType)

  const rangeMaxDate = useMemo(() => {
    if (maxRangeDays && dateRangeSelected.from) {
      const maxByRange = new Date(dateRangeSelected.from)
      maxByRange.setDate(maxByRange.getDate() + maxRangeDays)
      if (maxDate && maxByRange > maxDate) return maxDate
      return maxByRange
    }
    return maxDate
  }, [maxRangeDays, dateRangeSelected.from, maxDate])

  const initialCalendarDate = useMemo(() => {
    const today = new Date()
    if (maxDate && today > maxDate) return maxDate
    if (minDate && today < minDate) return minDate
    return today
  }, [minDate, maxDate])

  const isRenderSelectMonthly =
    (dateType === 'range' && menuSelected === 'selectMonthly') ||
    (dateType === 'single' && monthPickerOnly)

  return (
    <>
      <Box width="100%">
        <Fieldset
          placeholder={placeholder}
          date={date}
          disabled={disabled}
          handleOpen={handleOpen}
          customIcon={rest.customIcon}
          isError={!!errorMessage}
        />
        {errorMessage && (
          <Typography variant="smallRegular" color={theme.palette.error.main}>
            {errorMessage}
          </Typography>
        )}
      </Box>
      <S.DatePickerMenu anchorEl={anchorEl} open={isOpen} onClose={handleClose}>
        <Box width="fit-content" display="flex">
          {!(monthPickerOnly || hideLeftMenu) && (
            <LeftMenu
              title={title}
              dateType={dateType}
              dateDefaultValue={dateDefaultValue}
              showDate={showDate}
              menuSelected={menuSelected}
              disabledMenu={leftMenuProp?.disabledMenu || []}
              handleSelectMenu={handleSelectMenu}
            />
          )}
          {showDate && (
            <S.DateWrapper>
              {dateType === 'single' && !monthPickerOnly && (
                <Calendar
                  {...rest}
                  minDate={minDate}
                  maxDate={maxDate}
                  date={dateSelected || initialCalendarDate}
                  onChange={handleSelectDate}
                />
              )}
              {dateType === 'range' && menuSelected !== 'selectMonthly' && (
                <DateRangePicker
                  ranges={[
                    {
                      startDate: dateRangeSelected.from || new Date(),
                      endDate: dateRangeSelected.to || new Date(),
                      key: 'selection',
                    },
                  ]}
                  {...(menuSelected === 'selectWeekly' && {
                    focusedRange: [0, 0],
                  })}
                  months={months}
                  direction="horizontal"
                  onChange={handleSelectDateRange}
                  preventSnapRefocus
                  minDate={minDate}
                  maxDate={rangeMaxDate}
                />
              )}
              {isRenderSelectMonthly && (
                <MonthPicker
                  monthSelected={monthSelected}
                  setMonthSelected={setMonthSelected}
                  yearSelected={yearSelected}
                  setYearSelected={setYearSelected}
                  minDate={minDate}
                  maxDate={maxDate}
                />
              )}
            </S.DateWrapper>
          )}
        </Box>
        <S.Action>
          <Button variant="outlined" onClick={handleReset}>
            Reset
          </Button>
          <Button variant="contained" onClick={handleSubmit}>
            Terapkan
          </Button>
        </S.Action>
      </S.DatePickerMenu>
    </>
  )
}

// extend from DatePicker component above
// make it simpler for single date selection use case

type DatePickerSinglePropsType = {
  label?: string
  value: string
  minDate?: string
  onChange: (value: string) => void
}

const DATE_VALUE_FORMAT: DateFormatType = 'YYYY-MM-DD'

export const DatePickerSingle = ({
  label,
  value,
  minDate,
  onChange,
}: DatePickerSinglePropsType) => {
  const today = new Date()

  return (
    <Box>
      {!!label && <Typography variant="body2">{label}</Typography>}
      <DatePicker
        value={value ? new Date(value) : undefined}
        dateType="single"
        placeholder="Pilih Tanggal"
        dateDisplayFormat="DD/MM/YYYY"
        minDate={minDate ? stringToDateObject(minDate, DATE_VALUE_FORMAT) : today}
        onChange={(date) => {
          onChange(date ? dateFormat({ date, format: DATE_VALUE_FORMAT }) : '')
        }}
        hideLeftMenu
      />
    </Box>
  )
}

export default DatePicker
